<template>
  <div>
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <h3>
          {{ $t('settings.project.personas.name') }}
        </h3>
        <hr>
        <p>
          {{ $t('settings.project.personas.description') }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="!hasSomethingToSet" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <b-form-text>
          {{ $t('settings.project.personas.noFieldsState') }}
        </b-form-text>
      </b-col>
    </b-row>

    <b-row v-else-if="personas.length === 0" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <b-form-text>
          {{ $t('settings.project.personas.emptyState') }}
        </b-form-text>
      </b-col>
    </b-row>

    <b-row
      v-for="persona in personas"
      :key="persona.id"
      class="mb-3 align-items-center"
    >
      <b-col cols="12" offset-sm="1" sm="6">
        <b-form-input
          :value="persona.name"
          @change="renamePersona(persona.id, $event)"
        />
        <b-form-text>
          {{ $t('settings.project.personas.idHint') }}
          <code>{{ persona.id }}</code>
        </b-form-text>
      </b-col>
      <b-col cols="6" sm="2">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="toggleAnswers(persona.id)"
        >
          {{ $t('settings.project.personas.answersButton') }}
        </b-button>
      </b-col>
      <b-col cols="6" sm="2">
        <b-button
          variant="outline-danger"
          size="sm"
          @click="confirmRemovePersona(persona.id)"
        >
          {{ $t('settings.project.personas.deleteButton') }}
        </b-button>
      </b-col>

      <!-- What this persona will really put into the preview, in words. Built
           from the SAME reading that fills the preview, so the line can never
           promise an answer the editor would not see. text-break for the same
           reason the segment sentence has it: a value can be one long word. -->
      <b-col cols="12" offset-sm="1" sm="10" class="mt-1">
        <p class="mb-0 text-break">
          {{ summaryFor(persona) }}
        </p>
      </b-col>

      <!-- The answers themselves, opened underneath the persona they belong to
           rather than in a modal: this is a long list of boxes and it is the
           whole content of the panel, not a decision to be taken in front of
           it. The boxes are the very ones the editor's preview panel shows
           (editor-v2/components/canvas/AnswerPreviewPanel.vue) and they are
           built from the same model, so a persona can hold exactly what the
           preview can be set to -- no more and no less. -->
      <b-col v-if="editingPersonaId === persona.id" cols="12" offset-sm="1" sm="10" class="mt-3">
        <div class="persona-answers">
          <section v-for="group in answerGroups" :key="group.label" class="pa-group">
            <h4>{{ group.label }}</h4>
            <div v-for="field in group.fields" :key="field.path" class="pa-field">
              <span class="pa-label">{{ field.label }}</span>

              <!-- Multiple choice: one answer can hold several values -->
              <div v-if="isMulti(field)" class="pa-ticks">
                <label v-for="option in field.options || []" :key="option.value" class="pa-tick">
                  <input
                    type="checkbox"
                    :checked="isTicked(persona, field, option.value)"
                    @change="onTick(persona, field, option.value, $event)"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <!-- Single choice -->
              <select
                v-else-if="field.options && field.options.length"
                class="form-control"
                :value="boxValue(persona, field)"
                @change="onBoxInput(persona, field, $event)"
              >
                <option value="">(nicht beantwortet)</option>
                <option v-for="option in field.options" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>

              <!-- Yes / no -->
              <select
                v-else-if="isBoolean(field)"
                class="form-control"
                :value="boxValue(persona, field)"
                @change="onBoxInput(persona, field, $event)"
              >
                <option value="">(nicht beantwortet)</option>
                <option value="true">Ja</option>
                <option value="false">Nein</option>
              </select>

              <input
                v-else
                class="form-control"
                :type="isNumber(field) ? 'number' : 'text'"
                :value="boxValue(persona, field)"
                placeholder="(nicht beantwortet)"
                @input="onBoxInput(persona, field, $event)"
              >
            </div>
          </section>

          <section v-if="forms.length" class="pa-group">
            <h4>Zuletzt gesehene Formularseite</h4>
            <div v-for="form in forms" :key="form.formId" class="pa-field">
              <span class="pa-label">{{ form.label }}</span>
              <input
                class="form-control pa-short"
                type="number"
                min="1"
                :value="lastPageOf(persona, form.formId)"
                placeholder="1"
                @input="onLastPage(persona, form.formId, $event)"
              >
            </div>
            <p class="pa-hint">
              Bei einem mehrseitigen Formular merkt sich der Leser, wo er stehen geblieben ist.
            </p>
          </section>

          <section v-if="sets.length" class="pa-group">
            <h4>Personalisierung</h4>
            <div class="pa-field">
              <span class="pa-label">Zu welchem Set gehört dieser Leser?</span>
              <select
                class="form-control"
                :value="persona.personalizationSetId || ''"
                @change="onPersonalizationSet(persona, $event)"
              >
                <option value="">(zu keinem)</option>
                <option v-for="set in sets" :key="set.id" :value="set.id">{{ set.label }}</option>
              </select>
            </div>
            <div v-for="entry in setFields" :key="entry.id" class="pa-field">
              <span class="pa-label">{{ entry.label }}</span>
              <input
                class="form-control"
                type="text"
                :value="persona.personalizationValues[entry.id] || ''"
                @input="onPersonalizationValue(persona, entry.id, $event)"
              >
            </div>
          </section>

          <section class="pa-group">
            <h4>Adresszeile</h4>
            <div class="pa-field">
              <span class="pa-label">Parameter, mit denen der Leser ankommt</span>
              <input
                class="form-control"
                type="text"
                placeholder="kampagne=sommer&amp;quelle=mail"
                :value="persona.urlQuery"
                @input="onUrlQuery(persona, $event)"
              >
            </div>
          </section>

          <b-button variant="outline-secondary" size="sm" @click="closeAnswers">
            {{ $t('settings.project.personas.doneButton') }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="hasSomethingToSet" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="5">
        <b-form-input
          v-model="newPersonaName"
          :placeholder="$t('settings.project.personas.namePlaceholder')"
          @keyup.enter="submitNewPersona"
        />
        <b-form-text v-if="duplicateWarning" class="text-danger">
          {{ $t('settings.project.personas.duplicateNameWarning') }}
        </b-form-text>
      </b-col>
      <b-col cols="12" sm="3">
        <b-button
          variant="outline-primary"
          @click="submitNewPersona"
        >
          {{ $t('settings.project.personas.addButton') }}
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/**
 * "Testpersonen": a named set of answers ("Große Behörde, hat schon
 * digitalisiert") that fills the editor's "Vorschau mit Antworten" on one
 * click, instead of the redakteur typing the same imagined reader again every
 * time they want to look at a page through their eyes.
 *
 * The Vue-2 twin of editor-v2/components/canvas/AnswerPreviewPanel.vue, and
 * deliberately built on the very same modules, so both halves of the product
 * offer the same questions, coerce a typed answer the same way and read a
 * persona back identically:
 *
 *   conditionFields.js     the field list every condition is built from
 *   answerPersonaModel.js  reading that list as answers, coercing a typed
 *                          value, and what a persona is worth today
 *
 * WHAT IS STORED is `theme_settings.answerPersonas`, beside
 * `theme_settings.segments` and saved by the very same path: this panel owns
 * only its own list and emits it, and ProjectSettings.vue merges it into the
 * shared object (updateAnswerPersonas). No new column, no new permission --
 * the Segments tab has been writing this column since Task 7.
 *
 * TWO KINDS OF TEXT on this page, on purpose. The frame -- headings, buttons,
 * the empty states -- goes through $t like every other project settings tab.
 * The answer boxes themselves are hardcoded German, word for word the same as
 * in the editor's preview panel (which has no translation layer at all): the
 * two lists have to read identically, and half of one of them translated would
 * be worse than the one language this product has.
 */
import { cloneDeep, isEqual } from 'lodash';
import slugify from '@sindresorhus/slugify';
import { collectConditionFields } from '@/components/modals/conditionFields';
import {
  coercePreviewValue,
  isBooleanAnswerField,
  isMultiAnswerField,
  isNumberAnswerField,
  normalizePersonas,
  personaSummary,
  previewAnswerGroups,
  previewPersonalizationFields,
  previewPersonalizationSets,
  splitAnswerPath,
  togglePreviewChoice,
} from '@/components/modals/answerPersonaModel';

export default {
  name: 'ProjectSettingsPersonas',
  props: {
    themeSettings: {
      type: Object,
      default: () => ({}),
    },
    // The project's own personalization sets and forms — the same two props
    // the Segments tab is given, from the same two apollo queries in
    // ProjectSettings.vue. The questions a persona can answer are made of
    // these.
    personalization: {
      type: Array,
      default: () => [],
    },
    formsData: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    personas: [],
    newPersonaName: '',
    duplicateWarning: false,
    // Id of the persona whose answers are open underneath it, or null.
    editingPersonaId: null,
  }),
  created() {
    this.loadFromThemeSettings();
  },
  watch: {
    // The same rule the Segments tab needed (see its own watcher): this panel
    // keeps a working copy, and "Abbrechen" in the surrounding view refetches
    // the project and rebuilds `themeSettings` from the database. Without
    // following that, a cancelled deletion would be written back for good on
    // the next change. The comparison first, because the parent echoes the
    // very list this panel just sent on every emit.
    themeSettings() {
      const incoming = normalizePersonas(
        this.themeSettings && this.themeSettings.answerPersonas,
      );
      if (isEqual(incoming, this.personas)) return;
      this.loadFromThemeSettings();
    },
  },
  computed: {
    // Everything a persona can answer, in the ONE list the editor's condition
    // panel and the preview panel are built from. WITHOUT segments: a segment
    // is a rule, not something a reader answers, and the preview panel does
    // not offer it either.
    //
    // An empty entry is filtered out for the same reason as in the Segments
    // tab: collectConditionFields reads `form.form_data` straight away, and a
    // null in the list would throw during RENDER, costing the whole panel and
    // every healthy persona on it.
    //
    // `allowUrlParameterAlone` is deliberately NOT set, exactly as the editor
    // leaves it: with no form and no personalization set there is nothing for
    // a persona to answer, and the address bar alone is a condition, not a
    // reader.
    conditionFields() {
      return collectConditionFields({
        forms: (this.formsData || []).filter((form) => form && typeof form === 'object'),
        personalizationSets: this.personalization,
      });
    },
    answerGroups() {
      return previewAnswerGroups(this.conditionFields);
    },
    sets() {
      return previewPersonalizationSets(this.conditionFields);
    },
    // The forms that have at least one question, for the "last page" boxes.
    // Same derivation as the preview panel's, from the same groups.
    forms() {
      const seen = [];
      this.answerGroups.forEach((group) => {
        const first = group.fields[0];
        const parts = first ? splitAnswerPath(first.path) : null;
        if (parts && !seen.some((entry) => entry.formId === parts.formId)) {
          seen.push({ formId: parts.formId, label: group.label });
        }
      });
      return seen;
    },
    hasSomethingToSet() {
      return this.answerGroups.length > 0 || this.sets.length > 0;
    },
    editingPersona() {
      return this.personas.find((persona) => persona.id === this.editingPersonaId) || null;
    },
    setFields() {
      const persona = this.editingPersona;
      return previewPersonalizationFields(
        this.conditionFields, persona ? persona.personalizationSetId : null,
      );
    },
  },
  methods: {
    // Builds the working copy from the prop, healed into the shape everything
    // here expects.
    //
    // Healing only, never SAVING the healing — deliberately unlike the Segments
    // tab, which emits on load when it had to migrate a shape. It has to: a
    // segment stuck on the old array shape evaluates as "always true" for
    // every visitor, so leaving it in the database is dangerous. Nothing of
    // the kind can happen to a persona: both halves heal on every read (the
    // editor through collectAnswerPersonas), so an old entry is harmless where
    // it lies. Emitting here would mark the whole project as changed the
    // moment somebody merely opens this tab, and the shared changed-flag turns
    // that into a save button appearing over work nobody did.
    loadFromThemeSettings() {
      this.personas = normalizePersonas(
        cloneDeep((this.themeSettings && this.themeSettings.answerPersonas) || []),
      );
    },
    submitNewPersona() {
      this.addPersona(this.newPersonaName);
    },
    // The id is derived from the name exactly once, on creation, and never
    // again — the same rule a segment's id follows. Nothing references a
    // persona by id today, but the editor's panel keys its buttons on it and a
    // changing id would be a changing identity.
    addPersona(name) {
      const trimmedName = (name || '').trim();
      if (!trimmedName) return;
      const id = this.idFromName(trimmedName);
      if (!id || this.personas.some((persona) => persona.id === id)) {
        this.duplicateWarning = true;
        return;
      }
      this.duplicateWarning = false;
      this.personas.push({
        id,
        name: trimmedName,
        answers: {},
        personalizationSetId: null,
        personalizationValues: {},
        urlQuery: '',
      });
      this.newPersonaName = '';
      this.emitChange();
    },
    idFromName(name) {
      return slugify(name, { separator: '_' });
    },
    // An empty name is refused, and the BOX IS PUT BACK. Without the redraw
    // the two would disagree in silence: the stored name is still there, the
    // box the redakteur emptied still looks empty, and the panel is showing a
    // name nothing holds. The box is bound with :value (not v-model, which
    // would let an empty name through into the working copy), so Vue has no
    // reason of its own to redraw a row whose data did not change.
    renamePersona(id, newName) {
      const trimmedName = (newName || '').trim();
      const persona = this.personas.find((entry) => entry.id === id);
      if (!persona) return;
      if (!trimmedName) {
        this.$forceUpdate();
        return;
      }
      persona.name = trimmedName;
      this.emitChange();
    },
    confirmRemovePersona(id) {
      this.$bvModal.msgBoxConfirm(this.$t('settings.project.personas.deleteConfirmText'), {
        title: this.$t('settings.project.personas.deleteConfirmTitle'),
        okTitle: this.$t('buttons.ok'),
        cancelTitle: this.$t('buttons.cancel'),
        centered: true,
      }).then((confirmed) => {
        if (confirmed) this.removePersona(id);
      }).catch((err) => {
        console.log(err);
      });
    },
    removePersona(id) {
      this.personas = this.personas.filter((persona) => persona.id !== id);
      if (this.editingPersonaId === id) this.editingPersonaId = null;
      this.emitChange();
    },
    toggleAnswers(id) {
      this.editingPersonaId = this.editingPersonaId === id ? null : id;
    },
    closeAnswers() {
      this.editingPersonaId = null;
    },

    // -----------------------------------------------------------------------
    // The boxes. Every decision below — which control a field gets, what a
    // typed value means, what an empty box means — is taken from
    // answerPersonaModel.js, the module the editor's preview panel asks the
    // same questions of.
    // -----------------------------------------------------------------------

    isMulti(field) {
      return isMultiAnswerField(field.fieldType);
    },
    isBoolean(field) {
      return isBooleanAnswerField(field.fieldType);
    },
    isNumber(field) {
      return isNumberAnswerField(field.fieldType);
    },
    entryFor(persona, formId) {
      if (!persona.answers[formId]) {
        this.$set(persona.answers, formId, { values: {}, lastPage: null });
      }
      return persona.answers[formId];
    },
    storedValue(persona, field) {
      const parts = splitAnswerPath(field.path);
      if (!parts) return undefined;
      const entry = persona.answers[parts.formId];
      return entry ? entry.values[parts.fieldId] : undefined;
    },
    boxValue(persona, field) {
      const value = this.storedValue(persona, field);
      if (value === undefined || value === null) return '';
      return String(value);
    },
    isTicked(persona, field, optionValue) {
      const value = this.storedValue(persona, field);
      return Array.isArray(value) && value.map(String).indexOf(optionValue) !== -1;
    },
    // `undefined` REMOVES the answer rather than storing an empty one — the
    // same rule the reader's own store keeps (src/forms-renderer/answers.js).
    // It matters for conditions that ask whether a question was answered at
    // all: a stored `undefined` would answer "yes".
    setAnswer(persona, field, value) {
      const parts = splitAnswerPath(field.path);
      if (!parts) return;
      const entry = this.entryFor(persona, parts.formId);
      if (value === undefined) this.$delete(entry.values, parts.fieldId);
      else this.$set(entry.values, parts.fieldId, value);
      this.emitChange();
    },
    onBoxInput(persona, field, event) {
      this.setAnswer(persona, field, coercePreviewValue(field.fieldType, event.target.value));
    },
    onTick(persona, field, optionValue, event) {
      this.setAnswer(persona, field, togglePreviewChoice(
        this.storedValue(persona, field), optionValue, event.target.checked,
      ));
    },
    lastPageOf(persona, formId) {
      const entry = persona.answers[formId];
      const page = entry ? entry.lastPage : null;
      return page === null || page === undefined ? '' : String(page + 1);
    },
    // Shown counting from 1, stored counting from 0 — the reader counts pages
    // from 0 (src/forms-renderer/answers.js#setPage).
    onLastPage(persona, formId, event) {
      const raw = event.target.value;
      const num = Number(raw);
      const entry = this.entryFor(persona, formId);
      entry.lastPage = (raw === '' || !Number.isFinite(num) || num < 1) ? null : num - 1;
      this.emitChange();
    },
    // The persona handed in IS the entry in `this.personas` the row was
    // rendered from — writing through it is writing into this panel's own
    // working copy, which is why the rule against touching a parameter is
    // waived here and nowhere that a real copy would be meant.
    onPersonalizationSet(persona, event) {
      // eslint-disable-next-line no-param-reassign
      persona.personalizationSetId = event.target.value || null;
      // The values belong to the set that was chosen; keeping them across a
      // change would feed the new set the old set's fields.
      // eslint-disable-next-line no-param-reassign
      persona.personalizationValues = {};
      this.emitChange();
    },
    onPersonalizationValue(persona, fieldId, event) {
      this.$set(persona.personalizationValues, fieldId, event.target.value);
      this.emitChange();
    },
    onUrlQuery(persona, event) {
      // eslint-disable-next-line no-param-reassign
      persona.urlQuery = event.target.value;
      this.emitChange();
    },

    // What this persona is worth TODAY, measured against the field list as it
    // is now — a question the redakteur deleted afterwards is named as missing
    // instead of being shown as an answer that no longer arrives.
    summaryFor(persona) {
      return personaSummary(persona, this.conditionFields);
    },
    emitChange() {
      this.$emit('change', { answerPersonas: this.personas });
    },
  },
};
</script>

<style scoped>
.persona-answers { border-left: 3px solid #e9eded; padding: .25rem 0 .25rem 1rem; }
.pa-group { margin-bottom: 1.25rem; }
.pa-group h4 { margin: 0 0 .5rem; font-size: .9rem; color: #0e6b67; }
.pa-field { display: block; margin-bottom: .6rem; max-width: 28rem; }
.pa-label { display: block; color: #5b6668; margin-bottom: .2rem; font-size: .85rem; }
.pa-short { width: 6rem; }
.pa-ticks { display: flex; flex-direction: column; gap: .2rem; }
.pa-tick { display: flex; align-items: center; gap: .4rem; margin: 0; }
.pa-hint { color: #5b6668; margin: 0; font-size: .85em; }
</style>
